import React, { useState, useEffect } from 'react';
import { LoginForm } from './forms/LoginForm';
import { RegisterForm } from './forms/RegisterForm';
import { ForgotPasswordForm } from './forms/ForgotPasswordForm';
import { ResetPasswordForm } from './forms/ResetPasswordForm';
import { RegistrationSuccessPage } from './pages/RegistrationSuccessPage';
import * as authService from '../services/auth';

type AuthMode = 'login' | 'register' | 'forgot-password' | 'reset-password' | 'registration-success';

interface AuthPageProps {
  resetToken?: string; // 从URL参数获取的重置令牌
}

export const AuthPage: React.FC<AuthPageProps> = ({ resetToken }) => {
  const [mode, setMode] = useState<AuthMode>(
    resetToken ? 'reset-password' : 'login'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [invitationCode, setInvitationCode] = useState<string>('');

  // 从URL参数中获取邀请码
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      setInvitationCode(code);
      // 如果有邀请码参数，自动切换到注册模式
      if (!resetToken) {
        setMode('register');
      }
    }
  }, [resetToken]);

  const handleForgotPassword = async (email: string) => {
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(email);
      // 成功后不需要额外处理，ForgotPasswordForm会显示成功状态
    } catch (err) {
      setError(err instanceof Error ? err.message : '发送重置链接失败');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (token: string, newPassword: string) => {
    setLoading(true);
    setError('');
    try {
      await authService.resetPassword(token, newPassword);
      // 成功后不需要额外处理，ResetPasswordForm会显示成功状态
    } catch (err) {
      setError(err instanceof Error ? err.message : '密码重置失败');
    } finally {
      setLoading(false);
    }
  };

  const switchToLogin = () => {
    setMode('login');
    setError('');
  };

  const switchToRegister = () => {
    setMode('register');
    setError('');
  };

  const switchToForgotPassword = () => {
    setMode('forgot-password');
    setError('');
  };

  const handleRegistrationSuccess = (email: string) => {
    setRegistrationEmail(email);
    setMode('registration-success');
    setError('');
  };

  const handleLoginNeedVerification = (email: string) => {
    setRegistrationEmail(email);
    setMode('registration-success');
    setError('');
  };

  const renderAuthForm = () => {
    switch (mode) {
      case 'register':
        return (
          <RegisterForm
            onSwitchToLogin={switchToLogin}
            onRegistrationSuccess={handleRegistrationSuccess}
            initialInvitationCode={invitationCode}
          />
        );
      
      case 'forgot-password':
        return (
          <ForgotPasswordForm
            onSubmit={handleForgotPassword}
            loading={loading}
            error={error}
            onBackToLogin={switchToLogin}
          />
        );
      
      case 'reset-password':
        return resetToken ? (
          <ResetPasswordForm
            token={resetToken}
            onSubmit={handleResetPassword}
            loading={loading}
            error={error}
            onBackToLogin={switchToLogin}
          />
        ) : (
          <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">无效的重置链接</h2>
            <p className="text-gray-600 mb-6">
              重置链接无效或已过期，请重新申请密码重置。
            </p>
            <button
              onClick={switchToForgotPassword}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              重新申请重置密码
            </button>
          </div>
        );
      
      case 'registration-success':
        return (
          <RegistrationSuccessPage
            email={registrationEmail}
            onBackToLogin={switchToLogin}
          />
        );
      
      default: // login
        return (
          <LoginForm
            onSwitchToRegister={switchToRegister}
            onForgotPassword={switchToForgotPassword}
            onNeedVerification={handleLoginNeedVerification}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">PromptMaster</h1>
          <p className="text-gray-600">个人提示词管理平台</p>
        </div>
        
        {renderAuthForm()}
      </div>
    </div>
  );
}; 